import FormComponent from '../FormComponent';
import SelectorEngine from '../../utils/dom/selector-engine';
import EventHandler from '../../utils/dom/event-handler';
import { isEmpty } from 'lodash-es';
import { addInvalidClasses, removeInvalidClasses } from '../../utils/validateFormElement';

/* 	
	Example step config:
	{
		default: true,
		dependentQuestion: [2],
		errorMessage: 'Please upload a file to proceed',
		fieldName: 'user',
		acceptedFileTypes: ['image/jpeg', 'image/png', 'image/gif'],
		allowMultipleFileUpload: true,
		instructionText: 'Please upload a photo of yourself and any documentation related to your learning.',
		label: 'Upload your documentation',
		id: 1,
		options: [],
		question: 'any documentation related to your learning',
		questionPrefix: 'Please upload',
		questionType: 'fileUpload',
		buttonText: 'Browse files'
		step: 1,
		visible: true
	}
*/

const CHANGE_EVENT = 'change';

class FileUploadComponent extends FormComponent {
	constructor(containerElement, config) {
		super(containerElement, config);
		this.classPrefix = 'lta-form-fileupload';
		this.errorMessage = config.data.errorMessage || 'Please upload a file to proceed';
		this.initEventListeners(config.data);
	}

	markup(value, data, titleMarkup) {
		const acceptedFiles = data.acceptedFileTypes.join(', ');
		return `
      <fieldset>
        <legend>${titleMarkup}</legend>
        <div class="lta-search-input-field u-mb-2">
          <div id="lta-fileupload-error"></div>
          <div>
            <div class="u-mb-4">
              <p class="u-text-grey-lta u-mb-1 lta-text-input__label">${data.label}</p>
              <p class="u-body2 u-text-grey-dark-slate u-mb-1">${data.instructionText}</p>
							<div class="lta-form-fileupload__container">
								<p id="lta-fileupload-selected-files" class="u-mb-3"><span>No file chosen</span></p>
								<input
									class="lta-form-fileupload__input"
									type="file" id="${data.id}"
									value="${data.value}"
									name="${data.id}"
									accept="${acceptedFiles}"
									${data.allowMultipleFileUpload ? 'multiple' : ''}
								/>
								<label class="lta-btn lta-btn--default lta-btn--secondary" for="${data.id}">${data.buttonText}</label>
								</div>
            </div>
          </div>
        </div>
      </fieldset>
    `;
	}

	initEventListeners() {
		const fileUploadElements = SelectorEngine.find('input[type="file"]', this.containerEl);

		// eslint-disable-next-line unicorn/no-array-for-each
		fileUploadElements.forEach((fileUploadElement) => {
			EventHandler.on(fileUploadElement, CHANGE_EVENT, () => {
				this.validate();
			});
		});
	}

	validate() {
		const fileUpload = SelectorEngine.find('input[type="file"]', this.containerEl);
		const errorElement = SelectorEngine.findOne('#lta-fileupload-error', this.containerEl);

		if (isEmpty(fileUpload[0].value)) {
			this.setError(errorElement, this.errorMessage);
			return false;
		} else {
			const files = [...fileUpload[0].files];
			// Are any of the files over 5mb
			const isOversized = files.every((file) => file.size > 5_000_000);

			// Checking file format for validation
			const isAccepetdFiles = files.every((file) => this.data.acceptedFileTypes?.includes(file.type));

			if(!isAccepetdFiles) {
				const errorText = 'Incorrect file format';
				this.setError(errorElement, errorText);
				return false;
			}
			else if (isOversized) {
				const errorText = 'File is too large';
				this.setError(errorElement, errorText);
				return false;
			} else {
				this.clearError(errorElement);
				this.updateSelectedFilesDisplay(files);
				this.updateValue(files);
				return true;
			}
		}
	}

	setError(errorElement, errorMessage) {
		errorElement.textContent = errorMessage;
		addInvalidClasses(errorElement);
	}

	clearError(errorElement) {
		errorElement.textContent = '';
		removeInvalidClasses(errorElement);
	}

	updateSelectedFilesDisplay(files) {
		const selectedFilesElement = SelectorEngine.findOne('#lta-fileupload-selected-files', this.containerEl);
		selectedFilesElement.innerHTML = files.map((file) => `<span>${file.name}</span>`).join(', ');
	}

	updateValue(files) {
		this.value = {
			id: this.data.id,
			fieldName: this.data.fieldName,
			value: files
		};

		// NOTE: Currently only supporting one dependent question per fileUpload group
		this.dependentQuestion = this.data.dependentQuestion;
	}
}

export default FileUploadComponent;
